import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@/components/Icon';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/session';
import { hasPermission } from '@/lib/permissions';
import {
  actionsForKind,
  daysFromToday,
  distinctCapital,
  formatCapital,
  formatExpiryContext,
  groupByLens,
  hasRepEmail,
  LABEL_BY_LENS,
  reasonTagForFinding,
  narrowAuditFinding,
  totalCapital,
  type AuditActionMeta,
  type AuditFinding,
  type AuditFindingAction,
  type AuditFindingWithProduct,
  type AuditLens,
  type AuditProduct,
} from '@/lib/audit';

// ============================================================
// AuditPage — Phase 4, Epic 4B
// ============================================================
// First real iteration of the audit surface. Replaces the
// "lands in Phase 4" stub. Implements the minimal viewing
// surface ratified across the 4B detector + action tickets:
//
//   4B.1 + 4B.3 (2026-05-22) — dead_stock, over_par
//   4B.2       (2026-05-22) — mismatch (+ mismatch_note inline)
//   4B.4       (2026-05-23) — expiring_soon, expired (+ days
//                             context inline on the meta line)
//   4B.6       (2026-05-24) — per-row action surface (tap row
//                             to expand accordion; action bar
//                             with kind-conditional buttons;
//                             confirmation modal for Dispose)
//   4B.5       (2026-05-29) — hero + section tabs from the demo
//                             HTML. Gradient hero with per-kind
//                             capital breakdown; pill-tab bar
//                             (one tab per kind present, demo
//                             idiom); single active section.
//
// Page structure:
//   - Header (icon + "Audit" + "Scan now" button gated on
//     audit.scan.invoke)
//   - Gradient hero: total capital tied up + per-lens breakdown
//   - Headline-finding callout: highest-priority lens, plain
//     language (generalizes the demo's "legacy items" headline)
//   - Section tabs: one per display LENS with findings, ordered by
//     LABEL_BY_LENS.order; shows shortLabel + count + subtotal.
//     dead_stock + mismatch collapse into one "Recoverable capital"
//     lens (Block D), de-duped per product.
//   - Active section renders rows for the selected lens only
//   - Row: product name + reason tag + meta line + per-finding capital
//   - Tap row → accordion expand → action bar below
//     (Email rep is always present; kind-specific actions in
//     the middle; Dismiss last)
//   - Email rep opens a mailto: with kind-templated subject +
//     body; recipient blank (no vendor.rep_email field today)
//   - Dispose shows a confirmation modal ("Write off N units
//     of <name> ($X)? This cannot be undone.")
//   - Other actions execute on tap, refetch on success
//   - Empty state when no open findings
//   - "Most recent finding" relative-time footer
//
// 5-kinds-into-tabs decision (4B.5): the demo had a fixed
// 3-bucket model, but the shipped detectors produce 5 kinds
// (dead_stock, over_par, mismatch, expiring_soon, expired).
// Tabs are driven off the kinds actually present rather than
// forced to the demo's 3, so mismatch/expired findings are
// never silently dropped. See DECISIONS 2026-05-29.
//
// Out of scope (slot in later 4B subtickets):
//   - 4B.2a mismatch marker UI
//   - 4B.4a expiration-date marker UI
//   - vendors.rep_email field (Email rep recipient prefill)
//   - In-app email send via Resend (current is mailto: only)
//   - Undo paths for resolve actions
//
// Cross-kind cascade (per 4B.6 fork G):
//   Resolve actions (stop_reorder, dispose, use_now)
//   auto-resolve sibling open findings on the same product
//   in the same transaction (handled SQL-side). Dismiss is
//   per-finding. The PWA just refetches; the cascade shows
//   up as the sibling rows disappearing from the page.
//
// Detection RPC contract:
//   `detect_audit_findings(p_clinic_id)` returns void; the
//   page refetches audit_findings on resolution. Action RPC
//   `resolve_audit_finding(finding_id, action)` similarly
//   returns void; page refetches after.
//
// Refresh strategy:
//   Refetch on mount, refetch after a successful scan,
//   refetch after a successful action. No live subscription.
// ============================================================

interface FetchedData {
  /** All open findings for the active clinic, sorted newest-first. */
  findings: readonly AuditFinding[];
  /** Product lookup keyed by id; covers every product referenced
   *  by a finding row. Built client-side from a separate fetch
   *  (mirrors ReorderPage / InventoryPage pattern — composite-FK
   *  PostgREST embeds intentionally avoided per project convention). */
  productById: Map<string, AuditProduct>;
}

type FetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; data: FetchedData };

type ScanState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'error'; message: string };

const SCAN_BUTTON_BASE_CLASSES =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition';
const SCAN_BUTTON_ENABLED_CLASSES =
  'bg-action text-white hover:bg-action-hover active:bg-action-active';
const SCAN_BUTTON_DISABLED_CLASSES =
  'bg-slate-100 text-slate-400 cursor-not-allowed';

export default function AuditPage() {
  const session = useSession();
  const clinicId = session.activeMembership.clinic_id;

  // PWA gate matches the SQL has_permission() result. RPC
  // re-checks server-side; this hides the affordance for roles
  // that can't act on it. Reads effective permissions so a
  // staff/viewAs session correctly mirrors the impersonated role.
  const canScan = hasPermission(session, 'audit.scan.invoke');

  const [state, setState] = useState<FetchState>({ kind: 'loading' });
  const [scan, setScan] = useState<ScanState>({ kind: 'idle' });
  // Bump to trigger a refetch — used by handleScan after the RPC
  // resolves. Mirrors the in-effect cancellation pattern from
  // ReorderPage / InventoryPage: load lives inside the effect
  // with a `cancelled` flag closed over, and any external
  // refresh trigger goes through this token.
  const [refetchToken, setRefetchToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });

    async function load(): Promise<void> {
      const [findingsResult, productsResult, vendorsResult] = await Promise.all([
        supabase
          .from('audit_findings')
          .select(
            'id, clinic_id, product_id, kind, capital_tied_up, status, detected_at, dismissed_at, dismissed_by, notes',
          )
          .eq('clinic_id', clinicId)
          .eq('status', 'open')
          .order('detected_at', { ascending: false }),
        supabase
          .from('products')
          .select(
            'id, name, count, par, unit_price, uom, mismatch_note, expiration_date, vendor_id',
          )
          .eq('clinic_id', clinicId),
        // Vendors carry rep_email, which drives the "Email rep"
        // action. Fetched here so the mailto can address the
        // product's vendor rep; a product with no vendor rep on
        // file gets the action disabled (see actionsForKind gating
        // in the row). Read-only select — is_clinic_member RLS.
        supabase
          .from('vendors')
          .select('id, rep_email')
          .eq('clinic_id', clinicId),
      ]);

      if (cancelled) return;

      if (findingsResult.error) {
        console.error('[stocora] Audit findings fetch failed:', findingsResult.error);
        setState({ kind: 'error', message: findingsResult.error.message });
        return;
      }
      if (productsResult.error) {
        console.error('[stocora] Audit products fetch failed:', productsResult.error);
        setState({ kind: 'error', message: productsResult.error.message });
        return;
      }
      if (vendorsResult.error) {
        console.error('[stocora] Audit vendors fetch failed:', vendorsResult.error);
        setState({ kind: 'error', message: vendorsResult.error.message });
        return;
      }

      const narrowed: AuditFinding[] = [];
      for (const row of findingsResult.data ?? []) {
        const item = narrowAuditFinding(row);
        if (item) narrowed.push(item);
      }

      // vendor_id -> rep_email (nullable). Products join to this by
      // vendor_id to resolve the rep address for the Email rep
      // action.
      const repEmailByVendor = new Map<string, string | null>();
      for (const v of vendorsResult.data ?? []) {
        repEmailByVendor.set(v.id, v.rep_email);
      }

      const productById = new Map<string, AuditProduct>();
      for (const p of productsResult.data ?? []) {
        productById.set(p.id, {
          id: p.id,
          name: p.name,
          count: p.count,
          par: p.par,
          unit_price: p.unit_price,
          uom: p.uom,
          mismatch_note: p.mismatch_note,
          expiration_date: p.expiration_date,
          rep_email: repEmailByVendor.get(p.vendor_id) ?? null,
        });
      }

      setState({ kind: 'loaded', data: { findings: narrowed, productById } });
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [clinicId, refetchToken]);

  const handleScan = useCallback(async (): Promise<void> => {
    if (!canScan) return;
    setScan({ kind: 'running' });
    const { error } = await supabase.rpc('detect_audit_findings', {
      p_clinic_id: clinicId,
    });
    if (error) {
      console.error('[stocora] Audit scan failed:', error);
      const msg =
        error.code === '42501'
          ? "You don't have permission to run audit scans"
          : "Couldn't run scan — try again";
      setScan({ kind: 'error', message: msg });
      return;
    }
    setScan({ kind: 'idle' });
    setRefetchToken((n) => n + 1);
  }, [canScan, clinicId]);

  // ---- Action state (4B.6) ----
  // Single-expand model: at most one row open at a time. Tapping
  // an already-open row collapses it. Tapping a different row
  // swaps the expansion. State holds the finding id, or null.
  const [expandedFindingId, setExpandedFindingId] = useState<string | null>(null);

  // In-flight action by finding id. Disables the action bar
  // buttons during the RPC round-trip. Most actions are
  // sub-second so we don't show a spinner — just disable.
  const [pendingAction, setPendingAction] = useState<{
    findingId: string;
    action: AuditFindingAction;
  } | null>(null);

  // Error toast for action failures. Cleared on next successful
  // action or on tap. Kept simple — full toast system would be
  // its own ticket.
  const [actionError, setActionError] = useState<string | null>(null);

  // Confirmation modal state for Dispose. The PWA only confirms
  // for destructive actions per fork H; other actions execute
  // immediately on tap.
  const [pendingDispose, setPendingDispose] = useState<AuditFindingWithProduct | null>(null);

  // Block C: which finding just had a rep inquiry sent (transient
  // per-row "Inquiry sent" confirmation; the finding stays open).
  const [emailRepSentFor, setEmailRepSentFor] = useState<string | null>(null);

  // ---- Section tab state (4B.5) ----
  // Which kind's findings are visible. null means "not yet
  // resolved" — an effect below pins it to the first available
  // section once findings load (and re-pins if the current
  // section empties out after an action cascade). Tabs are driven
  // off the display LENSES present (Block D): dead_stock + mismatch
  // collapse into one "Recoverable capital" tab, so up to 4 tabs.
  const [activeSection, setActiveSection] = useState<AuditLens | null>(
    null,
  );

  const handleToggleExpand = useCallback((findingId: string) => {
    setExpandedFindingId((prev) => (prev === findingId ? null : findingId));
  }, []);

  const handleAction = useCallback(
    async (row: AuditFindingWithProduct, action: AuditFindingAction): Promise<void> => {
      // Email rep (Block C): send server-side AS THE CLINIC via the
      // send-rep-email function — no more mailto: (which opened the
      // machine's default mail client). A native confirm guards the
      // send since it mails a vendor as the clinic; the finding stays
      // open (this is an inquiry, not a resolution).
      if (action === 'email_rep') {
        const rep = row.product.rep_email;
        const confirmed = window.confirm(
          `Send a buyback inquiry to ${rep ?? 'the vendor rep'} about ${row.product.name}?`,
        );
        if (!confirmed) return;

        setPendingAction({ findingId: row.finding.id, action });
        setActionError(null);

        const { data, error } = await supabase.functions.invoke<{
          ok: boolean;
          sent_to?: string;
          vendor?: string;
          error?: string;
        }>('send-rep-email', { body: { finding_id: row.finding.id } });

        setPendingAction(null);

        if (error || !data?.ok) {
          console.error('[stocora] Email rep failed:', error ?? data?.error);
          const reason = data?.error;
          setActionError(
            reason === 'insufficient_permission'
              ? "You don't have permission to do that"
              : reason === 'no_rep_email'
                ? 'No sales-rep email on file for this vendor'
                : "Couldn't send the email — try again",
          );
          return;
        }

        setActionError(null);
        setEmailRepSentFor(row.finding.id);
        return;
      }

      // Dispose: confirmation modal first.
      if (action === 'dispose' && pendingDispose?.finding.id !== row.finding.id) {
        setPendingDispose(row);
        return;
      }

      setPendingAction({ findingId: row.finding.id, action });
      setActionError(null);

      const { error } = await supabase.rpc('resolve_audit_finding', {
        p_finding_id: row.finding.id,
        p_action: action,
      });

      setPendingAction(null);

      if (error) {
        console.error('[stocora] Audit action failed:', error);
        const msg =
          error.code === '42501'
            ? "You don't have permission to do that"
            : error.code === '22013'
              ? "That action isn't applicable for this finding kind"
              : error.code === 'P0002'
                ? "This finding was already closed — refreshing"
                : "Couldn't complete the action — try again";
        setActionError(msg);
        // Refetch defensively on P0002 (out-of-sync state) to
        // resync UI with reality.
        if (error.code === 'P0002') setRefetchToken((n) => n + 1);
        return;
      }

      // Success — collapse the row and refetch. Cascade resolves
      // (stop_reorder / dispose / use_now) cause sibling findings
      // to drop off the page on the refetch.
      setExpandedFindingId(null);
      setPendingDispose(null);
      setRefetchToken((n) => n + 1);
    },
    [pendingDispose],
  );

  const handleDisposeConfirm = useCallback(async (): Promise<void> => {
    if (!pendingDispose) return;
    await handleAction(pendingDispose, 'dispose');
  }, [pendingDispose, handleAction]);

  const handleDisposeCancel = useCallback(() => {
    setPendingDispose(null);
  }, []);

  // Build the joined-and-grouped view once per state change. The
  // groupBy / total computations are O(n) over findings; tiny at
  // pilot scale but memoized for cleanliness.
  const view = useMemo(() => {
    if (state.kind !== 'loaded') return null;
    const { findings, productById } = state.data;
    const joined: AuditFindingWithProduct[] = [];
    for (const finding of findings) {
      const product = productById.get(finding.product_id);
      if (!product) {
        // Defensive: shouldn't happen given the composite FK on
        // audit_findings(product_id, clinic_id) → products. A
        // missing product would indicate a deletion racing the
        // page fetch; quietly skip rather than crash.
        continue;
      }
      joined.push({ finding, product });
    }
    return {
      joined,
      grouped: groupByLens(joined),
      // Hero total counts each product ONCE (distinctCapital) so a
      // product flagged under two kinds isn't double-counted. With
      // the recoverable-capital lens now de-duping per product, the
      // per-lens subtotals also count each product once, so they no
      // longer exceed the hero total — the old overlap footnote is
      // retired (Block D, 2026-07-14).
      total: distinctCapital(joined),
      lastScannedAt: findings.length > 0 ? findings[0]!.detected_at : null,
    };
  }, [state]);

  // Keep activeSection valid: pin to the first available group on
  // first load, and re-pin if the active section empties out after
  // an action cascade resolves its last finding. groupByLens
  // returns lenses in LABEL_BY_LENS.order, so [0] is the
  // highest-priority non-empty section.
  const groupedLenses = view?.grouped.map((g) => g.lens) ?? [];
  const groupedLensesKey = groupedLenses.join(',');
  useEffect(() => {
    const lenses = groupedLensesKey ? (groupedLensesKey.split(',') as AuditLens[]) : [];
    if (lenses.length === 0) {
      setActiveSection((prev) => (prev === null ? prev : null));
      return;
    }
    setActiveSection((prev) =>
      prev !== null && lenses.includes(prev) ? prev : lenses[0]!,
    );
  }, [groupedLensesKey]);

  const activeGroup =
    view?.grouped.find((g) => g.lens === activeSection) ?? null;

  return (
    <div className="px-4 py-4 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {/* Update #9 (2026-07-17): screen renamed Audit → Inventory
              Health (the spec's name for this screen throughout; nav
              short-label is "Health"). Title in Newsreader serif per
              §1 typography — titles only, never data. */}
          <Icon name="heart" className="w-5 h-5 text-slate-500" />
          <h1 className="text-lg font-serif font-semibold text-slate-800">
            Inventory Health
          </h1>
        </div>
        {canScan && (
          <button
            type="button"
            onClick={() => void handleScan()}
            disabled={scan.kind === 'running' || state.kind === 'loading'}
            className={`${SCAN_BUTTON_BASE_CLASSES} ${
              scan.kind === 'running' || state.kind === 'loading'
                ? SCAN_BUTTON_DISABLED_CLASSES
                : SCAN_BUTTON_ENABLED_CLASSES
            }`}
          >
            {scan.kind === 'running' ? (
              <>
                <span
                  className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"
                  aria-hidden
                />
                Scanning…
              </>
            ) : (
              <>
                <Icon name="refresh" className="w-3.5 h-3.5" />
                Scan now
              </>
            )}
          </button>
        )}
      </div>

      {scan.kind === 'error' && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs">
          {scan.message}
        </div>
      )}

      {state.kind === 'loading' && (
        <div className="flex items-center justify-center py-12">
          <span
            className="w-7 h-7 border-[3px] border-slate-200 border-t-action rounded-full animate-spin"
            role="status"
            aria-label="Loading audit findings"
          />
        </div>
      )}

      {state.kind === 'error' && (
        <div className="text-center text-red-600 text-xs py-12">
          Couldn't load audit findings: {state.message}
        </div>
      )}

      {state.kind === 'loaded' && view && view.joined.length === 0 && (
        <EmptyState canScan={canScan} />
      )}

      {state.kind === 'loaded' && view && view.joined.length > 0 && (
        <>
          <AuditHero
            grouped={view.grouped}
            total={view.total}
          />
          <HeadlineFinding grouped={view.grouped} />
          {actionError && (
            <button
              type="button"
              onClick={() => setActionError(null)}
              className="mt-3 w-full px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs text-left"
            >
              {actionError}
            </button>
          )}
          <SectionTabs
            grouped={view.grouped}
            activeSection={activeSection}
            onSelect={setActiveSection}
          />
          <div className="mt-3">
            {activeGroup && (
              <FindingsSection
                lens={activeGroup.lens}
                rows={activeGroup.rows}
                expandedFindingId={expandedFindingId}
                pendingAction={pendingAction}
                emailRepSentFor={emailRepSentFor}
                onToggleExpand={handleToggleExpand}
                onAction={handleAction}
              />
            )}
          </div>
          {view.lastScannedAt && (
            <p className="text-[11px] text-slate-400 text-center mt-6">
              Most recent finding · {formatRelativeTime(view.lastScannedAt)}
            </p>
          )}
        </>
      )}
      {pendingDispose && (
        <DisposeConfirmModal
          row={pendingDispose}
          pending={pendingAction?.action === 'dispose' && pendingAction.findingId === pendingDispose.finding.id}
          onConfirm={() => void handleDisposeConfirm()}
          onCancel={handleDisposeCancel}
        />
      )}
    </div>
  );
}

function EmptyState({ canScan }: { canScan: boolean }) {
  return (
    <div className="text-center text-slate-500 py-12">
      <Icon name="trending" className="w-10 h-10 mx-auto mb-3 text-slate-300" />
      <p className="text-sm font-medium text-slate-700">No findings</p>
      <p className="text-xs mt-1">
        {canScan
          ? 'Tap "Scan now" to check for dead stock and over-par products.'
          : 'No open audit findings for this clinic.'}
      </p>
    </div>
  );
}

// ============================================================
// 4B.5 — Hero + section tabs from the demo HTML, wired to real
// computed findings.
//
// Demo had a fixed 3-bucket model (Dead stock / Expiring /
// Over-par). The shipped detectors are 5 kinds (4B.1–4B.4):
// dead_stock, over_par, mismatch, expiring_soon, expired. Block D
// (2026-07-14) collapses dead_stock + mismatch into one
// "Recoverable capital" display lens, so the hero breakdown and
// tab bar are driven off the LENSES present (LABEL_BY_LENS.order),
// de-duped per product — no finding is silently dropped, and the
// subtotals reconcile with the total. See DECISIONS 2026-07-14.
// ============================================================

interface LensGroup {
  readonly lens: AuditLens;
  readonly rows: readonly AuditFindingWithProduct[];
}

function AuditHero({
  grouped,
  total,
}: {
  grouped: readonly LensGroup[];
  total: number;
}) {
  // Update #9 §2 (2026-07-17): dark hero — `dark` token (#163031),
  // one of exactly two sanctioned dark surfaces in the app. Total
  // figure is DM Mono in the sanctioned coral (#D9603F) — the ONE
  // deliberate coral bleed in the whole palette — and pulls from
  // the live Block-D merged total, never a hardcoded number.
  // Sub-stats below in smaller DM Mono. Replaces the interim
  // slate/indigo gradient from 4B.5.
  return (
    <div className="rounded-2xl bg-dark text-white p-4 shadow-card relative overflow-hidden">
      <div className="relative">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/60">
          <Icon name="trending" className="w-3.5 h-3.5" strokeWidth={2.25} />
          Onboarding audit
        </div>
        <div className="mt-1 text-[11px] text-white/70">
          Capital tied up in flagged inventory
        </div>
        <div className="text-[40px] font-mono font-medium text-coral leading-none mt-1 tabular-nums">
          {formatCapital(total)}
        </div>
        <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]">
          {grouped.map(({ lens, rows }) => (
            <div key={lens}>
              <div className="text-white/60">{LABEL_BY_LENS[lens].label}</div>
              <div className="text-white font-mono font-medium text-[13px] tabular-nums">
                {formatCapital(totalCapital(rows))}
              </div>
            </div>
          ))}
        </div>
        {/* No overlap footnote (Block D): the recoverable-capital
            lens de-dups per product, so each lens subtotal counts a
            product once and the subtotals reconcile with the total. */}
      </div>
    </div>
  );
}

function HeadlineFinding({ grouped }: { grouped: readonly LensGroup[] }) {
  // Surface the highest-priority section as a plain-language
  // callout. Recoverable capital leads (LABEL_BY_LENS.order = 1)
  // when present; we generalize to whichever lens sorts first so
  // the callout is always meaningful regardless of which detectors
  // fired.
  const lead = grouped[0];
  if (!lead) return null;
  const count = lead.rows.length;
  const capital = totalCapital(lead.rows);
  const label = LABEL_BY_LENS[lead.lens].label.toLowerCase();
  // Update #9 (2026-07-17): amber → gold status hue; tint is the
  // same hue at low opacity per §1, not a separate lighter color.
  return (
    <div className="mt-3 p-3 rounded-xl bg-gold/[.08] border border-gold/20 flex items-start gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-gold/15 flex items-center justify-center shrink-0">
        <Icon name="bell" className="w-4 h-4 text-gold" strokeWidth={2} />
      </div>
      <div className="text-[12px] text-gold leading-snug flex-1">
        <div className="font-semibold">
          {count} {count === 1 ? 'item' : 'items'} flagged as {label}
        </div>
        <div className="text-gold/90 mt-0.5">
          {formatCapital(capital)} tied up here — your largest flagged bucket.
          Review the items below and dismiss, dispose, or stop reordering.
        </div>
      </div>
    </div>
  );
}

function SectionTabs({
  grouped,
  activeSection,
  onSelect,
}: {
  grouped: readonly LensGroup[];
  activeSection: AuditLens | null;
  onSelect: (lens: AuditLens) => void;
}) {
  return (
    <div
      className="mt-4 flex items-center gap-1 p-1 bg-slate-100 rounded-lg"
      role="tablist"
      aria-label="Audit finding categories"
    >
      {grouped.map(({ lens, rows }) => {
        const active = lens === activeSection;
        return (
          <button
            key={lens}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(lens)}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-md text-[11px] font-medium transition ${
              active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
            }`}
          >
            <div className="flex items-center gap-1.5">
              <span>{LABEL_BY_LENS[lens].shortLabel}</span>
              <span
                className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                  active
                    ? 'bg-slate-100 text-slate-700'
                    : 'bg-slate-200 text-slate-600'
                }`}
              >
                {rows.length}
              </span>
            </div>
            <div
              className={`text-[10px] tabular-nums ${
                active ? 'text-slate-500' : 'text-slate-400'
              }`}
            >
              {formatCapital(totalCapital(rows))}
            </div>
          </button>
        );
      })}
    </div>
  );
}

interface RowInteractionProps {
  readonly expandedFindingId: string | null;
  readonly pendingAction: { findingId: string; action: AuditFindingAction } | null;
  readonly emailRepSentFor: string | null;
  readonly onToggleExpand: (findingId: string) => void;
  readonly onAction: (row: AuditFindingWithProduct, action: AuditFindingAction) => void;
}

function FindingsSection({
  lens,
  rows,
  expandedFindingId,
  pendingAction,
  emailRepSentFor,
  onToggleExpand,
  onAction,
}: {
  lens: AuditLens;
  rows: readonly AuditFindingWithProduct[];
} & RowInteractionProps) {
  // No section header here — the active tab in SectionTabs already
  // names the lens and shows its count + capital subtotal.
  return (
    <section role="tabpanel" aria-label={LABEL_BY_LENS[lens].label}>
      <ul className="space-y-1">
        {rows.map((row) => (
          <FindingRow
            key={row.finding.id}
            row={row}
            expanded={expandedFindingId === row.finding.id}
            pendingAction={
              pendingAction?.findingId === row.finding.id ? pendingAction.action : null
            }
            emailRepSent={emailRepSentFor === row.finding.id}
            onToggleExpand={onToggleExpand}
            onAction={onAction}
          />
        ))}
      </ul>
    </section>
  );
}

function FindingRow({
  row,
  expanded,
  pendingAction,
  emailRepSent,
  onToggleExpand,
  onAction,
}: {
  row: AuditFindingWithProduct;
  expanded: boolean;
  pendingAction: AuditFindingAction | null;
  emailRepSent: boolean;
  onToggleExpand: (findingId: string) => void;
  onAction: (row: AuditFindingWithProduct, action: AuditFindingAction) => void;
}) {
  const { finding, product } = row;
  // Meta line varies by kind. Three shapes:
  //
  //   over_par:                "<count> on hand · par <X>"
  //   expiring_soon / expired: "<count> <uom> on hand · <ctx>"
  //   default (dead, mismatch): "<count> <uom> on hand"
  let meta: string;
  if (finding.kind === 'over_par') {
    meta = `${product.count} on hand · par ${product.par ?? '—'}`;
  } else if (finding.kind === 'expiring_soon' || finding.kind === 'expired') {
    const days = daysFromToday(product.expiration_date);
    meta = `${product.count} ${product.uom} on hand · ${formatExpiryContext(finding.kind, days)}`;
  } else {
    meta = `${product.count} ${product.uom} on hand`;
  }

  // Email rep needs a vendor rep address. When absent, drop the
  // action (rather than open a recipient-less draft) and surface a
  // one-line pointer to Settings › Vendors instead. Decided
  // 2026-07-07: no rep on file => disabled, not a broken mailto.
  const repAvailable = hasRepEmail(product);
  const actions = actionsForKind(finding.kind).filter(
    (a) => a.action !== 'email_rep' || repAvailable,
  );

  // Reason tag (Block D): in the recoverable-capital lens, dead_stock
  // and mismatch findings sit side by side, so each row shows WHY it's
  // recoverable ("Slow-moving" vs "Discontinued"). Null for other kinds.
  const reasonTag = reasonTagForFinding(row);

  // Update #9 §2 (2026-07-17): "needs action today" treatment — full
  // rust outline (1.5px #9a3f2f), light rust-tinted background, solid
  // rust badge — visually outranks the calmer recoverable cards.
  // Applied lens-driven to expired + expiring_soon findings (the
  // spec's example is "expiring tomorrow"; the exact day-threshold is
  // mockup-ambiguous without the HTML file, so the whole expiry class
  // gets the urgent treatment for now — tighten when the mockup is in
  // hand if Drew wants a narrower window).
  const urgent = finding.kind === 'expired' || finding.kind === 'expiring_soon';

  return (
    <li
      className={
        urgent
          ? 'bg-rust/[.06] rounded-lg overflow-hidden border-[1.5px] border-rust'
          : 'bg-white border border-slate-200 rounded-lg overflow-hidden'
      }
    >
      <button
        type="button"
        onClick={() => onToggleExpand(finding.id)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 active:bg-slate-100 transition"
        aria-expanded={expanded}
      >
        <div className="min-w-0 flex-1 pr-3">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="text-sm text-slate-800 truncate">{product.name}</p>
            {urgent && (
              <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-rust text-white">
                {finding.kind === 'expired' ? 'Expired' : 'Act today'}
              </span>
            )}
            {reasonTag && (
              <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                {reasonTag}
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">{meta}</p>
          {finding.kind === 'mismatch' && product.mismatch_note && (
            <p className="text-[11px] text-slate-500 italic mt-0.5 truncate">
              {product.mismatch_note}
            </p>
          )}
        </div>
        <span className="text-sm font-mono font-medium text-slate-800 tabular-nums">
          {formatCapital(finding.capital_tied_up)}
        </span>
      </button>
      {expanded && (
        <>
          <ActionBar
            actions={actions}
            pendingAction={pendingAction}
            onAction={(action) => onAction(row, action)}
          />
          {emailRepSent && (
            <div className="px-3 pb-2.5 -mt-0.5 text-[11px] text-success inline-flex items-center gap-1">
              <Icon name="check" className="w-3.5 h-3.5" strokeWidth={2.5} />
              Inquiry sent to the vendor rep
            </div>
          )}
          {!repAvailable && (
            <div className="px-3 pb-2.5 -mt-0.5 text-[11px] text-slate-500 leading-snug">
              No sales-rep email on file for this vendor.{' '}
              <Link
                to="/settings/vendors"
                className="text-action font-medium hover:underline"
              >
                Add one in Settings
              </Link>{' '}
              to email them about this item.
            </div>
          )}
        </>
      )}
    </li>
  );
}

const ACTION_TONE_CLASSES: Record<AuditActionMeta['tone'], string> = {
  primary: 'bg-action text-white hover:bg-action-hover active:bg-action-active',
  destructive: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
  neutral: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 active:bg-slate-100',
};

function ActionBar({
  actions,
  pendingAction,
  onAction,
}: {
  actions: readonly AuditActionMeta[];
  pendingAction: AuditFindingAction | null;
  onAction: (action: AuditFindingAction) => void;
}) {
  const anyPending = pendingAction !== null;
  return (
    <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 flex flex-wrap gap-1.5">
      {actions.map((meta) => {
        const isPending = pendingAction === meta.action;
        const baseClasses =
          'inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed';
        return (
          <button
            key={meta.action}
            type="button"
            onClick={() => onAction(meta.action)}
            disabled={anyPending}
            className={`${baseClasses} ${ACTION_TONE_CLASSES[meta.tone]}`}
          >
            {isPending ? '…' : meta.label}
          </button>
        );
      })}
    </div>
  );
}

function DisposeConfirmModal({
  row,
  pending,
  onConfirm,
  onCancel,
}: {
  row: AuditFindingWithProduct;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { product } = row;
  const writeOffValue = product.count * product.unit_price;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dispose-modal-title"
    >
      <div className="w-full max-w-sm bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-4 py-4">
          <h3 id="dispose-modal-title" className="text-sm font-semibold text-slate-800">
            Write off {product.count} {product.uom} of {product.name}?
          </h3>
          <p className="text-xs text-slate-600 mt-2">
            This zeros out the on-hand count and writes a {formatCapital(writeOffValue)} loss to the inventory ledger. This cannot be undone.
          </p>
        </div>
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="px-3 py-1.5 rounded-md text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 active:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-50"
          >
            {pending ? 'Writing off…' : 'Write off'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Relative time formatting — small helper inlined here rather
// than carried in audit.ts. Single caller (the "Most recent
// finding" footer); promote if a second consumer lands.
// ============================================================
function formatRelativeTime(isoTimestamp: string): string {
  const then = new Date(isoTimestamp).getTime();
  const diffMs = Date.now() - then;
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return new Date(isoTimestamp).toLocaleDateString();
}
